// Offers.jsx — a discount on one product, for a window of dates.
//
// ── THE PREVIEW IS ADVISORY, AND SAYS SO ──
// The form works out what a customer would pay and shows it, but the server
// re-derives the base price from the catalogue and re-checks the ceiling on
// save. Those two can disagree: this screen's price lookup narrows to the
// caller's store, while the server's ceiling check is store-blind, so a store
// admin can see a base the server will not use. A 400 is therefore a normal
// outcome rather than a bug — offer_exceeds_max, no_price, offer_exists,
// offer_value_invalid — and the form shows what came back rather than
// pretending the preview was a promise.
//
// ── UNITS ──
// A percentage is a whole number; the server refuses a fraction rather than
// rounding it. A flat cut is in RUPEES here and the server converts to paise.
// Getting this backwards is a hundredfold error in a customer's favour.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "../../../api/axios";
import {
  fetchOffers,
  createOffer,
  updateOffer,
  deleteOffer,
  toggleOfferStatus,
  resetOfferState,
} from "../../../slice/offer/offerslice";
import StoreScopePicker, {
  defaultStoreScope,
  storeScopeOf,
} from "../component/StoreScopePicker";
import {
  PageShell,
  DataTable,
  Button,
  Pill,
  Toggle,
  RowActions,
  Modal,
  Field,
  Combo,
  Pager,
  PlusIcon,
} from "../component/AdminUi";

const PER_PAGE = 10;

const OFFER_TYPES = [
  { value: "percentage", label: "Percentage off" },
  { value: "price", label: "Flat amount off" },
];

/* Date -> yyyy-MM-dd for <input type="date">. */
const forInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const onDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const maxOfferOf = (product) =>
  Number(product?.maxOffer ?? product?.max_offer ?? 100) || 100;

const rupees = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const blank = {
  id: null,
  product: null,
  offerType: "percentage",
  offerValue: "",
  startDate: "",
  endDate: "",
  description: "",
  status: true,
};

export default function Offers() {
  const dispatch = useDispatch();
  const { offers, loading, error, pagination, success } = useSelector(
    (s) => s.offer
  );

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [scope, setScope] = useState(defaultStoreScope());
  const [basePrice, setBasePrice] = useState(0);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  /* The product picker's options, narrowed server-side as you type. */
  const [products, setProducts] = useState([]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /* Debounced: this fires on every keystroke in the search box. */
  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(fetchOffers({ page, limit: PER_PAGE, search: query }));
    }, 300);
    return () => clearTimeout(t);
  }, [dispatch, page, query]);

  useEffect(() => {
    if (success) dispatch(resetOfferState());
  }, [success, dispatch]);

  useEffect(() => {
    api
      .get("/admin/catalog/products?page=1&limit=100")
      .then((res) => {
        const rows = res.data?.data ?? res.data ?? [];
        setProducts(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setProducts([]));
  }, []);

  /* The base the preview is worked out from.
     Only prices in force right now count: a row with no `to` is open-ended
     and current, but `inForce` alone is also satisfied by a FUTURE-dated open
     row, so both tests are needed or the base is one the server will not
     use. */
  const loadBasePrice = useCallback(async (productId) => {
    try {
      const res = await api.get(`/admin/catalog/products/${productId}/prices`);
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const now = Date.now();
      const live = rows.filter(
        (r) =>
          (!r.from || new Date(r.from).getTime() <= now) &&
          (r.to == null ? r.inForce !== false : new Date(r.to).getTime() > now)
      );
      if (!live.length) return setBasePrice(0);
      // Paise on the wire, rupees on the screen.
      setBasePrice(Math.min(...live.map((r) => Number(r.pricePaise) || 0)) / 100);
    } catch {
      setBasePrice(0);
    }
  }, []);

  const preview = useMemo(() => {
    const value = Number(form.offerValue);
    if (!form.product || !value || basePrice === 0) return null;
    const after =
      form.offerType === "percentage"
        ? basePrice - (basePrice * value) / 100
        : basePrice - value;
    return { base: basePrice, after: Math.max(0, +after.toFixed(2)) };
  }, [form.product, form.offerType, form.offerValue, basePrice]);

  /* ── the form ──────────────────────────────────────────────────────────── */

  const openNew = () => {
    setForm(blank);
    setScope(defaultStoreScope());
    setBasePrice(0);
    setProblem("");
    setOpen(true);
  };

  const openEdit = async (offer) => {
    setForm({
      id: offer.id,
      product: offer.product,
      offerType: offer.offerType,
      offerValue: String(offer.offerValue),
      startDate: forInput(offer.startDate),
      endDate: forInput(offer.endDate),
      description: offer.description || "",
      status: offer.status,
    });
    setScope(storeScopeOf(offer));
    setProblem("");
    await loadBasePrice(offer.product.id);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setForm(blank);
    setScope(defaultStoreScope());
    setBasePrice(0);
    setProblem("");
  };

  const pickProduct = async (id) => {
    const product = products.find((p) => String(p.id) === String(id));
    setForm((f) => ({ ...f, product: product || null }));
    setProblem("");
    if (product) await loadBasePrice(product.id);
    else setBasePrice(0);
  };

  const save = async () => {
    const value = Number(form.offerValue);
    const ceiling = maxOfferOf(form.product);

    if (!form.product) return setProblem("Choose the product this applies to.");
    if (!value || value <= 0) return setProblem("The discount has to be more than zero.");

    if (form.offerType === "percentage") {
      if (value > 100) return setProblem("A percentage cannot be more than 100.");
      /* The server stores whole percent and refuses a fraction rather than
         rounding it (400 offer_value_invalid). */
      if (!Number.isInteger(value))
        return setProblem("A percentage has to be a whole number.");
      if (value > ceiling)
        return setProblem(`This product allows at most ${ceiling}% off.`);
    } else if (basePrice > 0) {
      const mostOff = +((basePrice * ceiling) / 100).toFixed(2);
      if (value > mostOff)
        return setProblem(`This product allows at most ${rupees(mostOff)} off.`);
    }

    if (!form.startDate) return setProblem("Pick the day it starts.");
    if (!form.endDate) return setProblem("Pick the day it ends.");
    if (new Date(form.endDate) < new Date(form.startDate))
      return setProblem("The end date cannot be before the start date.");
    if (!scope.availableInAllStores && scope.stores.length === 0)
      return setProblem("Choose at least one store, or make it estate-wide.");

    setSaving(true);
    setProblem("");

    /* createdBy / updatedBy are not sent — the server stamps the token's
       username, so a client-supplied name would only ever be a second,
       disagreeing opinion. */
    const body = {
      product: form.product.id,
      offerType: form.offerType,
      offerValue: value,
      startDate: form.startDate,
      endDate: form.endDate,
      description: form.description,
      status: form.status,
      availableInAllStores: scope.availableInAllStores,
      stores: scope.stores,
      excludedStores: scope.excludedStores,
    };

    try {
      if (form.id) await dispatch(updateOffer({ id: form.id, data: body })).unwrap();
      else await dispatch(createOffer(body)).unwrap();
      dispatch(fetchOffers({ page, limit: PER_PAGE, search: query }));
      close();
    } catch (e) {
      setProblem(e || "That did not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete the offer on ${row.product?.name || "this product"}?`))
      return;
    try {
      await dispatch(deleteOffer(row.id)).unwrap();
      dispatch(fetchOffers({ page, limit: PER_PAGE, search: query }));
    } catch (e) {
      setProblem(e || "Could not delete it.");
    }
  };

  const rows = offers || [];
  const live = rows.filter((o) => o.status !== false).length;

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <PageShell
      title="Offers"
      count={
        loading && !rows.length
          ? "Loading…"
          : pagination?.total != null
          ? `${pagination.total} offer${pagination.total === 1 ? "" : "s"} · ${live} running`
          : `${rows.length} shown`
      }
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        placeholder: "Search by product",
      }}
      actions={
        <Button kind="primary" onClick={openNew}>
          <PlusIcon />
          Add offer
        </Button>
      }
    >
      <DataTable
        columns={["Product", "Discount", "Running", "Live", ""]}
        loading={loading}
        error={error || problem}
        isEmpty={!loading && rows.length === 0}
        emptyTitle={query ? "No offer matches that" : "No offers yet"}
        emptyHint={
          query
            ? "Try the product name."
            : "An offer takes a percentage or a flat amount off one product."
        }
      >
        {rows.map((row) => (
          <tr key={row.id}>
            <td>
              <span className="ui-who__name">{row.product?.name || "—"}</span>
              {row.description ? (
                <div className="ui-muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {row.description}
                </div>
              ) : null}
            </td>
            <td className="ui-num">
              <Pill tone="gold">
                {row.offerType === "percentage"
                  ? `${row.offerValue}% off`
                  : `${rupees(row.offerValue)} off`}
              </Pill>
            </td>
            <td className="ui-num">
              {onDate(row.startDate)} – {onDate(row.endDate)}
            </td>
            <td>
              <Toggle
                on={row.status !== false}
                onChange={() => dispatch(toggleOfferStatus(row.id))}
                label={
                  row.status !== false
                    ? "Running — tap to stop"
                    : "Stopped — tap to run"
                }
              />
            </td>
            <td>
              <RowActions onEdit={() => openEdit(row)} onDelete={() => remove(row)} />
            </td>
          </tr>
        ))}
      </DataTable>

      <Pager
        page={page}
        totalPages={pagination?.totalPages}
        total={pagination?.total}
        perPage={PER_PAGE}
        onPage={setPage}
      />

      <Modal
        open={open}
        title={form.id ? "Edit offer" : "Add offer"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : form.id ? "Save changes" : "Add it"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field label="Product" required>
          <Combo
            value={form.product?.id || ""}
            onChange={pickProduct}
            options={products}
            placeholder="Choose a product"
          />
        </Field>

        <Field label="Kind of discount" required>
          <select
            className="ui-select"
            value={form.offerType}
            onChange={(e) =>
              setForm((f) => ({ ...f, offerType: e.target.value, offerValue: "" }))
            }
          >
            {OFFER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={form.offerType === "percentage" ? "Percent off" : "Rupees off"}
          required
          hint={
            form.product
              ? `This product allows up to ${maxOfferOf(form.product)}% off.`
              : undefined
          }
        >
          <input
            className="ui-input"
            value={form.offerValue}
            onChange={set("offerValue")}
            inputMode="decimal"
            placeholder={form.offerType === "percentage" ? "e.g. 10" : "e.g. 25"}
          />
        </Field>

        {preview ? (
          <div className="ui-alert ui-alert--note">
            <span>
              Was <strong>{rupees(preview.base)}</strong>, becomes{" "}
              <strong>{rupees(preview.after)}</strong>. The server re-checks this
              against the catalogue when you save, so it can still refuse.
            </span>
          </div>
        ) : null}

        <Field label="Running" required hint="Both days are included.">
          <div style={{ display: "flex", gap: 10 }}>
            <input
              className="ui-input"
              type="date"
              value={form.startDate}
              onChange={set("startDate")}
              aria-label="Start date"
            />
            <input
              className="ui-input"
              type="date"
              value={form.endDate}
              onChange={set("endDate")}
              aria-label="End date"
            />
          </div>
        </Field>

        <Field label="Note" hint="Shown under the product name on this screen.">
          <input
            className="ui-input"
            value={form.description}
            onChange={set("description")}
            placeholder="e.g. Diwali week"
          />
        </Field>

        <Field label="Stores">
          <StoreScopePicker value={scope} onChange={setScope} />
        </Field>
      </Modal>
    </PageShell>
  );
}
